import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useQuery } from 'js/hooks/useQuery';
import { fetchCycleTimeReport } from 'js/actions/cycle-time-report-actions';
import { fetchSprintReport } from 'js/actions/sprint-report-actions';
import { getTeamConnectorConfig } from 'js/connectors';
import { configuredRemovedStatuses } from './removedStatuses';
import JiraTeamPage from './TeamPage';

/**
 * Container for Jira team page: loads cycle time and sprint reports,
 * renders JiraTeamPage with the data.
 */
function JiraTeamPageContainer({
  teamConnectorConfigurations = [],
  fetchCycleTimeReport,
  fetchSprintReport,
}) {
  const { teamId, connectorId } = useParams();
  const config = getTeamConnectorConfig(
    teamConnectorConfigurations,
    connectorId
  );

  const shouldLoad = Boolean(connectorId && config);
  const removedStatuses = configuredRemovedStatuses(config?.configuration);

  // Reports treat the team's removed statuses as dropped work unless overridden here
  const [countRemovedAsCompleted, setCountRemovedAsCompleted] = useState(false);
  const reportParams = useMemo(
    () => (countRemovedAsCompleted ? { removed_statuses: '' } : {}),
    [countRemovedAsCompleted]
  );
  const querySuffix = countRemovedAsCompleted ? '-removed-as-completed' : '';

  const refreshCycleTimeReportQuery = useCallback(
    (queryId) => {
      if (shouldLoad) {
        return fetchCycleTimeReport(teamId, connectorId, queryId, reportParams);
      }
      return Promise.resolve();
    },
    [teamId, connectorId, shouldLoad, fetchCycleTimeReport, reportParams]
  );

  const {
    result: cycleTimeReport,
    refresh: refreshCycleTimeReport,
    hasLoaded: cycleTimeReportHasLoaded,
    error: cycleTimeReportError,
  } = useQuery({
    queryId: `cycleTimeReport-${teamId}-${connectorId || 'none'}${querySuffix}`,
    query: refreshCycleTimeReportQuery,
  });

  const refreshSprintReportQuery = useCallback(
    (queryId) => {
      if (shouldLoad) {
        return fetchSprintReport(teamId, connectorId, queryId, reportParams);
      }
      return Promise.resolve();
    },
    [teamId, connectorId, shouldLoad, fetchSprintReport, reportParams]
  );

  const {
    result: sprintReport,
    refresh: refreshSprintReport,
    hasLoaded: sprintReportHasLoaded,
    error: sprintReportError,
  } = useQuery({
    queryId: `sprintReport-${teamId}-${connectorId || 'none'}${querySuffix}`,
    query: refreshSprintReportQuery,
  });

  useEffect(() => {
    if (shouldLoad) {
      refreshCycleTimeReport();
      refreshSprintReport();
    }
  }, [shouldLoad, refreshCycleTimeReport, refreshSprintReport]);

  const reports = useMemo(
    () => ({
      cycleTime: {
        report: cycleTimeReport,
        isLoading: !cycleTimeReportHasLoaded,
        error: cycleTimeReportError,
      },
      sprints: {
        report: sprintReport,
        isLoading: !sprintReportHasLoaded,
        error: sprintReportError,
      },
    }),
    [
      cycleTimeReport,
      cycleTimeReportHasLoaded,
      cycleTimeReportError,
      sprintReport,
      sprintReportHasLoaded,
      sprintReportError,
    ]
  );

  const units = config?.configuration?.default_estimate_field || 'tasks';
  const isLoading = !cycleTimeReportHasLoaded || !sprintReportHasLoaded;
  const error = cycleTimeReportError || sprintReportError;

  return (
    <JiraTeamPage
      reports={reports}
      report={cycleTimeReport}
      isLoading={isLoading}
      error={error}
      units={units}
      removedStatuses={removedStatuses}
      countRemovedAsCompleted={countRemovedAsCompleted}
      onCountRemovedAsCompletedChange={setCountRemovedAsCompleted}
    />
  );
}

export default connect(null, {
  fetchCycleTimeReport,
  fetchSprintReport,
})(JiraTeamPageContainer);
